import React, { useState } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Input } from './components/ui/input';
import { Button } from './components/ui/button';
import { Label } from './components/ui/label';
import { User, Lock, Disc3, AlertCircle, ArrowRight, ShieldCheck, Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password);
      navigate('/');
    } catch (err) {
      setError('Nom d\'utilisateur ou mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Grille technique discrète */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(163,230,53,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(163,230,53,0.04) 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }}
      />
      {/* Halo lime */}
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[480px] w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-lime-400/10 blur-[120px]" />

      <main className="relative flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logotype */}
          <div className="mb-8 flex flex-col items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-lime-400/30 bg-gray-800/80 shadow-lg shadow-lime-400/5">
              <Disc3 className="h-7 w-7 text-lime-400 animate-[spin_12s_linear_infinite]" />
            </div>
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500">
              Générateur de fiches promotionnelles
            </p>
          </div>

          {/* Console de connexion */}
          <div className="relative overflow-hidden rounded-2xl border border-lime-400/40 bg-gray-800/90 shadow-2xl backdrop-blur-sm">
            {/* Liseré lumineux */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-lime-400 to-transparent" />

            <div className="p-8">
              <h1 className="text-center text-2xl font-bold text-lime-400" style={{ fontFamily: 'Raleway, sans-serif' }}>
                Kuroneko - Connexion
              </h1>
              <p className="mt-2 text-center text-sm text-gray-400">
                Saisissez vos identifiants pour accéder à l'outil
              </p>

              <form onSubmit={handleSubmit} className="mt-7 space-y-5">
                {error && (
                  <div
                    data-testid="login-error-message"
                    className="flex items-center gap-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300"
                  >
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="username" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-lime-400">
                    <User className="h-3.5 w-3.5" />
                    Nom d'utilisateur
                  </Label>
                  <Input
                    id="username"
                    data-testid="username-input"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoComplete="username"
                    placeholder="Admin"
                    className="h-12 border-gray-600 bg-gray-700/70 text-white transition-colors duration-200 placeholder:text-gray-500 focus:border-lime-400 focus-visible:ring-lime-400/40"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-lime-400">
                    <Lock className="h-3.5 w-3.5" />
                    Mot de passe
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      placeholder="••••••••"
                      className="h-12 border-gray-600 bg-gray-700/70 pr-12 text-white transition-colors duration-200 placeholder:text-gray-500 focus:border-lime-400 focus-visible:ring-lime-400/40"
                    />
                    <button
                      type="button"
                      data-testid="toggle-password-visibility"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-lime-400"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="login-submit-button"
                  disabled={loading}
                  className="group h-12 w-full gap-2 bg-lime-400 font-bold uppercase tracking-wider text-black transition-all duration-200 hover:-translate-y-0.5 hover:bg-lime-300 hover:shadow-lg hover:shadow-lime-400/20 disabled:translate-y-0 disabled:opacity-70"
                >
                  {loading ? (
                    <>
                      <Disc3 className="h-4 w-4 animate-spin" />
                      Connexion...
                    </>
                  ) : (
                    <>
                      Se connecter
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 flex items-center justify-center gap-2 border-t border-gray-700 pt-5 text-xs text-gray-500">
                <ShieldCheck className="h-3.5 w-3.5 text-lime-400/70" />
                Connexion sécurisée
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="relative flex flex-col items-center justify-between gap-2 border-t border-gray-700/60 px-6 py-4 text-xs text-gray-500 sm:flex-row">
        <span>© 2026 Kuroneko</span>
        <span className="flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5 text-lime-400/70" />
          Accès réservé au personnel autorisé
        </span>
      </footer>
    </div>
  );
};

export default Login;
